import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  fetchCustomerProfile,
  fetchCustomerShopAccounts,
  fetchPurchaseHistory,
  fetchRewardNFTs,
  fetchShopCapabilities,
  findShopIdByOwner,
  buildRegisterCustomerTx,
  buildCreateShopAccountTx,
  buildRedeemRewardNFTTx,
  buildCreateReferralTx,
} from '../../services/sui-service';
import { signAndExecuteTransaction, canSign, getSignDiagnostic } from '../../services/zklogin-signer';
import { startGoogleLogin, getDisplayName } from '../../services/zklogin-auth';
import { resolveStampIds, defaultResolverDeps } from '../../services/stuck-stamp-resolver';
import { confirmShopAccount } from '../../services/shop-account-verify';
import {
  createStampRequest, getStampCooldownRemaining, recordStampCooldown,
  updateStampOnchainStatus, getStuckStamps, claimSquarePayment, buildStampOnChainTx, type StampRequestRow,
} from '../../services/stamp-service';
import {
  updateRedeemOnchainStatus, getStuckRedeems, resolveConfirmedReveal, type RedeemRequestRow,
} from '../../services/redeem-service';
import { realtimeClient } from '../../services/realtime-client';
import { getShopByOwner } from '../../services/shop-service';
import { addressesEqual, resolveShopName, normalizeAddress } from '../../lib/shop-address';
import { currencyLabel } from '../../lib/currency-label';
import { birthdayInWindow } from '../../lib/birthday';
import { restoreSupabaseSession } from '../../services/supabase-auth';
import { completeQuest, purchaseLore, repairStreak, attributeReferral } from '../../services/gamification-service';
import { getLevelProgress, getLevelTitle, QUESTS, LORE_DATABASE, WISDOM_LINES, SECRET_LORE, stampsPerCard, type QuestProgressData, type LoreCheckData, type LoreEntry } from './bit-brew-constants';
import { getCatalog, getCatalogByIds, getMyCollectibles, getRedemptionCount, getEggBeans, getMyStreakState, getReferralBeans, getActiveSponsorFeature, getMintCounts, mergeCollection, type CollectionEntry, type SponsorFeature, type AwardedCollectible } from '../../services/collection-service';
import { apiFetch } from '../../services/api-client';
import { fetchBoard } from '../../services/arcade';
import { useKonami } from '../../hooks/useKonami';
import { useOnline } from '../../hooks/useOnline';

import BrewStation from './BrewStation';
import StampCard from './StampCard';
import ShopSpecials from './ShopSpecials';
import ShopBeans from './ShopBeans';
import ShopGoal from './ShopGoal';
import { fetchShopGoal } from '../../services/analytics-service';
import PixelPass from './PixelPass';
import AddToGoogleWallet from './AddToGoogleWallet';
import AddToAppleWallet from './AddToAppleWallet';
import LootBag from './LootBag';
import RewardCard from './RewardCard';
import BeanOfTheWeek from './BeanOfTheWeek';
import CollectibleReveal from './CollectibleReveal';
import QuestCarousel from './QuestCarousel';
import BadgeStrip from './BadgeStrip';
import LoreArchive from './LoreArchive';
import ShopModal from './ShopModal';
import LevelUpFlash from './LevelUpFlash';
import WelcomeFlash from './WelcomeFlash';
import StampFlash from './StampFlash';
import TabShell from './TabShell';
import { useTabParam } from './useTabParam';
import MorePanel from './MorePanel';
import PlayerName from './PlayerName';
import { firstStampStorageKey, isFirstStampEver, shopOwnerStorageKey } from './first-visit';
import { SessionRecoveryBanner } from './SessionRecoveryBanner';
import { isSessionError } from '../../services/session-errors';
import { plural } from '../../lib/plural';
import { CompBanner } from '../customer/CompBanner';
import ArcadeCard from '../arcade/ArcadeCard';
import ArcadePanel from '../arcade/ArcadePanel';

// ─── Types ──────────────────────────────────────────
type LogEntry = { id: number; text: string; color: string; timestamp: number };

const LOG_STORAGE_PREFIX = 'bitbrew_logs_';
const PURCHASED_LORE_PREFIX = 'bitbrew_purchased_lore_';
const selectedShopKey = (addr: string | null) =>
  addr ? `bitbrew_selected_shop:${addr}` : 'bitbrew_selected_shop';

// Offline header eyebrow (#10b): "8:12AM", no space before AM/PM — matches
// the mockup literal. toLocaleTimeString gives "8:12 AM"; strip the space.
function formatSyncTime(ts: number): string {
  return new Date(ts)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(' ', '');
}

// ═════════════════════════════════════════════════════
// DASHBOARD — BIT BREW CRT MONITOR LAYOUT
// ═════════════════════════════════════════════════════
function Dashboard() {
  const { userAddress, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // `locked` doesn't affect selectTab (only activeTab, which TabShell computes
  // itself) — this instance exists purely to hand a tab-switch callback down
  // into content Dashboard builds (e.g. StampCard.onOpenPass).
  const { selectTab } = useTabParam();
  // Connectivity (#10b). The only signal in the app today — see the hook's
  // own doc comment; no RPC-failure-based detector existed before this task.
  const online = useOnline();

  // ── State ──
  // Wall-clock of the last successful loadData() — drives the offline
  // header's "LAST SYNCED {time}" eyebrow. null until the first load lands.
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [customerProfile, setCustomerProfile] = useState<any>(null);
  const [shopAccounts, setShopAccounts] = useState<any[]>([]);
  const [rewardNFTs, setRewardNFTs] = useState<any[]>([]);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [allShops, setAllShops] = useState<any[]>([]);
  const [selectedShop, setSelectedShop] = useState<string | null>(
    () => localStorage.getItem(selectedShopKey(userAddress))
  );
  const [hasOnChainShop, setHasOnChainShop] = useState(false);
  // Task 2 (avatar chip): header chip's own avatarUrl — seeded by profile-get
  // below, and self-corrected by PlayerName's own profile-get (via
  // onAvatarChange) once MORE opens, in case the two drift.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stampingShop, setStampingShop] = useState<string | null>(null);
  const [showShopModal, setShowShopModal] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [stampGet, setStampGet] = useState<{ show: boolean; quantity: number }>({ show: false, quantity: 1 });
  const [purchasedLore, setPurchasedLore] = useState<string[]>([]);
  const [pendingRedeemNftIds, setPendingRedeemNftIds] = useState<string[]>([]);
  // The reward currently open at the counter, awaiting the barista's tap.
  const [counterRedeem, setCounterRedeem] = useState<
    { nftId: string; shopId: string; shopName: string; logoUrl?: string | null; itemTitle?: string | null } | null
  >(null);
  // The collectible awarded by the burn that just confirmed, shown in place
  // of the RewardCard until the customer dismisses it.
  const [reveal, setReveal] = useState<AwardedCollectible | null>(null);
  // Redeem row id of the reward currently open at the counter, set once the
  // barista's tap fires. resolveConfirmedReveal checks this AFTER its lookup
  // resolves, so a burn confirming after the modal has closed — or after a
  // new redemption has been opened — can't paint the wrong collectible.
  const counterRevealRequestId = useRef<string | null>(null);
  const logCounter = useRef(0);
  const prevLevel = useRef(0);
  const activeRequestId = useRef<string | null>(null);
  // Stuck stamps/redeems already retried this mount — recovery is once-per-row
  // per session so a persistent failure can't spin.
  const recoveryAttempted = useRef<Set<string>>(new Set());
  // Redeem rows whose burn has already been started this mount — see
  // burnRedeemOnChain for why three separate triggers converge on one row.
  const burnAttempted = useRef<Set<string>>(new Set());
  const [latestStampUpdate, setLatestStampUpdate] = useState<StampRequestRow | null>(null);
  const [latestRedeemUpdate, setLatestRedeemUpdate] = useState<RedeemRequestRow | null>(null);
  const [collection, setCollection] = useState<CollectionEntry[]>([]);
  const [redemptionCount, setRedemptionCount] = useState(0);
  const [mintCounts, setMintCounts] = useState<Record<string, number>>({});
  const [shopStreak, setShopStreak] = useState(0);
  const [streakRepairable, setStreakRepairable] = useState<string | null>(null);
  const [streakRepairSpent, setStreakRepairSpent] = useState(0);
  const [referralBeans, setReferralBeans] = useState(0);
  const [myBirthday, setMyBirthday] = useState<{ month: number; day: number } | null>(null);
  const [sponsorFeature, setSponsorFeature] = useState<SponsorFeature | null>(null);
  // Easter-egg bean grants (100th idle tap, Konami code) — server-owned,
  // summed from easter_egg_claims and folded into availableFunBeans below.
  const [eggBeans, setEggBeans] = useState(0);
  // Konami code unlocks a cosmetic secret lore card client-side; the bean
  // grant itself is still server-verified via the egg-claim action.
  const [konamiUnlocked, setKonamiUnlocked] = useState(false);
  // True once a signing attempt has failed in a way only a fresh login fixes —
  // surfaces the unified "RECONNECT" banner instead of stacking 4 red log lines.
  const [sessionBroken, setSessionBroken] = useState(false);
  // Selected shop's lore overrides (lore_mode='custom') applied over the
  // default set. Ids/unlock conditions never change — only the words.
  const [effectiveLore, setEffectiveLore] = useState<LoreEntry[]>(LORE_DATABASE);
  // Square ambiguous-match picker: multiple unclaimed till payments — the
  // customer says which one was theirs.
  const [squarePick, setSquarePick] = useState<{
    requestId: string;
    candidates: Array<{ id: string; amount_cents: number; currency: string; paid_at: string }>;
  } | null>(null);

  // Task 14: ARCADE card on HOME + its overlay. `arcadeOpen` seeds from the
  // ?panel=arcade deep link (useTabParam.ts routes that legacy param to the
  // HOME tab; this is what actually opens the overlay once HOME is there) —
  // read once at mount, same as every other query-driven flag in this file;
  // closing clears the param so a later refresh doesn't reopen it.
  const [arcadeOpen, setArcadeOpen] = useState(() => searchParams.get('panel') === 'arcade');
  // best/rank/tries all come from this week's global board — the same
  // fetchBoard call ArcadePanel itself makes on mount, so the teaser and the
  // panel never show numbers from two different sources. `triesAvailable`
  // rides along on the board response (api/arcade.ts's handleBoard computes
  // it exactly as `start` does, but read-only) rather than coming from
  // `start` itself — `start` is the only OTHER source of the number, and
  // calling it just to populate a HOME card would spend a try nobody asked
  // to spend. tries stays `null` (not 0, not a guessed nonzero) until the
  // board responds: `null` is the only state that isn't a false claim
  // either way — 0 would wrongly send an active player with tries left
  // toward "get stamped", and a guessed nonzero would wrongly tell a
  // spent-out player they can still play. ArcadeCard renders a neutral
  // placeholder for `null` rather than resolving to either.
  const [arcadeBest, setArcadeBest] = useState<number | null>(null);
  const [arcadeRank, setArcadeRank] = useState<number | null>(null);
  const [arcadeTries, setArcadeTries] = useState<number | null>(null);

  // Weekly shop goal (060). Server-computed: the week boundary is the SHOP's
  // Monday, and the total mixes rollup rows with today's live count — neither
  // is derivable from the shop row the client already holds.
  const [weeklyServed, setWeeklyServed] = useState(0);

  // Shared by the mount load and closeArcade below — I2 in the whole-branch
  // review: the HOME card used to fetch tries exactly once at mount and
  // never again, so spending all three runs and closing the panel still
  // read "3 RUNS" on the card. `cancelled` is only meaningful for the mount
  // call (StrictMode's double-invoke / an unmount racing the fetch); a call
  // from closeArcade fires while Dashboard is known to be mounted (the user
  // just interacted with it), so it always applies its result.
  const loadArcadeTeaser = useCallback((isCancelled: () => boolean = () => false) => {
    return fetchBoard('global')
      .then((result) => {
        if (isCancelled()) return;
        setArcadeBest(result.viewer?.score ?? null);
        setArcadeRank(result.viewer?.rank ?? null);
        setArcadeTries(result.triesAvailable);
      })
      .catch(() => { /* teaser only — ArcadePanel's own load is authoritative */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadArcadeTeaser(() => cancelled);
    return () => { cancelled = true; };
  }, [loadArcadeTeaser]);

  function closeArcade() {
    setArcadeOpen(false);
    // Deep-link hygiene: an unconsumed ?panel=arcade would reopen the panel
    // on the next refresh — the same reason selectTab deletes `panel` on
    // every tab switch (useTabParam.ts).
    if (searchParams.get('panel') === 'arcade') {
      const next = new URLSearchParams(searchParams);
      next.delete('panel');
      setSearchParams(next, { replace: true });
    }
    // Re-derive tries the same way the mount load did — ArcadePanel may have
    // just spent one (or several) via startRun, and this is the only signal
    // HOME gets that its cached count is now stale.
    loadArcadeTeaser();
  }

  useEffect(() => {
    let cancelled = false;
    if (!selectedShop) {
      setEffectiveLore(LORE_DATABASE);
      return;
    }
    getShopByOwner(selectedShop).then(shop => {
      if (cancelled) return;
      if (shop?.lore_mode === 'custom' && Array.isArray(shop.custom_lore) && shop.custom_lore.length > 0) {
        const overrides = new Map(shop.custom_lore.map(o => [o.id, o]));
        setEffectiveLore(LORE_DATABASE.map(entry => {
          const o = overrides.get(entry.id);
          return o ? { ...entry, title: o.title, text: o.text } : entry;
        }));
      } else {
        setEffectiveLore(LORE_DATABASE);
      }
    });
    return () => { cancelled = true; };
  }, [selectedShop]);

  // ── Persist selectedShop (per-user) ──
  useEffect(() => {
    const key = selectedShopKey(userAddress);
    if (selectedShop) {
      localStorage.setItem(key, selectedShop);
    } else {
      localStorage.removeItem(key);
    }
  }, [selectedShop, userAddress]);

  // ── If userAddress changes (account switch), re-load this user's selectedShop ──
  useEffect(() => {
    if (!userAddress) return;
    const stored = localStorage.getItem(selectedShopKey(userAddress));
    setSelectedShop(stored);
    // also drop any legacy global key from earlier builds
    localStorage.removeItem('bitbrew_selected_shop');
  }, [userAddress]);

  // ── Derived ──
  const currentAccount = shopAccounts.find((a: any) => addressesEqual(a.shop, selectedShop));
  const currentBeans = currentAccount?.points || 0;
  const globalXP = customerProfile?.total_xp || 0;
  const globalLevel = customerProfile?.level || 1;
  const { totalXP, nextThreshold } = getLevelProgress(globalXP, globalLevel);
  const xpPercent = nextThreshold > 0 ? Math.min(100, (totalXP / nextThreshold) * 100) : 100;
  const currentShopNFTs = rewardNFTs.filter((nft: any) => addressesEqual(nft.shop, selectedShop));
  // Storefront customization (040): the selected shop's tint + points word.
  // The balance (currentBeans) is shop-scoped, so its label follows the shop.
  const selectedShopRow = allShops.find((s: any) => addressesEqual(s.owner, selectedShop));

  // Only fetched when the shop actually has a goal switched on — a shop
  // without one must not pay for a round trip on every HOME render.
  const goalShopId = selectedShopRow?.supabase_id || selectedShopRow?.id;
  const goalEnabled = !!selectedShopRow?.weekly_goal?.enabled;
  useEffect(() => {
    if (!goalEnabled || !goalShopId) { setWeeklyServed(0); return; }
    let cancelled = false;
    fetchShopGoal(goalShopId)
      .then(r => { if (!cancelled) setWeeklyServed(r.served); })
      .catch(() => { /* a missing goal count must not break HOME */ });
    return () => { cancelled = true; };
  }, [goalEnabled, goalShopId]);

  const shopCurrency = currencyLabel(selectedShopRow);
  const shopAccent: string | null = selectedShopRow?.accent_color ?? null;
  // Birthday-week banner (041): shop has the perk on + my birthday is in window.
  const birthdayPerkActive = !!(
    myBirthday &&
    selectedShopRow?.birthday_perk?.enabled &&
    birthdayInWindow(myBirthday.month, myBirthday.day)
  );

  const STREAK_REPAIR_COST = 50; // display only — the server owns the real cost
  async function handleRepairStreak() {
    if (!streakRepairable || availableFunBeans < STREAK_REPAIR_COST) return;
    const result = await repairStreak(selectedShopRow?.supabase_id || selectedShopRow?.id);
    if (result) {
      setStreakRepairable(null);
      setStreakRepairSpent((prev) => prev + result.cost);
      setShopStreak((prev) => prev + 1);
      addLog(`STREAK SAVED! -${result.cost} ${shopCurrency}`, '#feca57');
    } else {
      addLog('STREAK REPAIR FAILED — TRY AGAIN', '#e52222');
    }
  }

  // Quest completion is DERIVED from current on-chain progress — never a
  // persisted running total. This means completions (and their reward beans)
  // always reflect reality: if on-chain state resets (e.g. a breaking contract
  // redeploy), stale completions vanish instead of lingering in localStorage.
  // Two passes: quests keyed on on-chain metrics first, then COMPLETIONIST,
  // which counts how many of those are done.
  const questBaseProgress: QuestProgressData = {
    totalPurchases: customerProfile?.total_purchases || 0,
    level: Math.max(0, globalLevel - 1),
    beans: currentBeans,
    nftCount: rewardNFTs.length,
    shopCount: shopAccounts.length,
    questsCompleted: 0, // COMPLETIONIST resolved in the second pass below
  };
  const primaryQuestsDone = QUESTS.filter(
    (q) => q.progressKey !== 'questsCompleted' && (questBaseProgress[q.progressKey] || 0) >= q.target,
  );
  const completedQuestIds = QUESTS.filter((q) =>
    q.progressKey === 'questsCompleted'
      ? primaryQuestsDone.length >= q.target
      : (questBaseProgress[q.progressKey] || 0) >= q.target,
  ).map((q) => q.id);
  const questRewardBeans = QUESTS
    .filter((q) => completedQuestIds.includes(q.id))
    .reduce((sum, q) => sum + q.reward, 0);

  // Fun beans: on-chain beans + quest rewards - lore purchases (gamification only)
  const spentOnLore = purchasedLore.reduce((sum, loreId) => {
    const lore = LORE_DATABASE.find(l => l.id === loreId);
    return sum + (lore?.cost || 0);
  }, 0);
  const availableFunBeans = Math.max(0, currentBeans + questRewardBeans + eggBeans + referralBeans - spentOnLore - streakRepairSpent);

  // Prefer the player's Google first name; fall back to a shortened address.
  // Raw hex addresses are the single most off-brand element in the product.
  const shortAddress = getDisplayName()
    || (userAddress ? `${userAddress.slice(0, 6)}...${userAddress.slice(-4)}` : null);

  // Quest progress data (from on-chain)
  const questProgressData: QuestProgressData = {
    totalPurchases: customerProfile?.total_purchases || 0,
    level: Math.max(0, globalLevel - 1),
    beans: currentBeans,
    nftCount: rewardNFTs.length,
    shopCount: shopAccounts.length,
    questsCompleted: completedQuestIds.length,
  };

  // Lore check data
  const loreCheckData: LoreCheckData = {
    level: globalLevel,
    totalPurchases: customerProfile?.total_purchases || 0,
    beans: currentBeans,
    nftCount: rewardNFTs.length,
    questsCompleted: completedQuestIds.length,
    shopCount: shopAccounts.length,
  };

  // ── Helpers ──
  const addLog = useCallback((text: string, color: string = '#fff') => {
    const id = logCounter.current++;
    setLogs((prev) => [{ id, text, color, timestamp: Date.now() }, ...prev].slice(0, 12));
  }, []);

  // ── Idle-tap easter egg: counts taps that hit an early-return in handleBrew
  // (no shop selected / cooldown). 5% chance of mug wisdom; the 100th tap
  // triggers a server-verified one-time bean grant. ──
  const bumpIdleTaps = useCallback(() => {
    if (!userAddress) return;
    const key = `bitbrew_idle_taps_${userAddress}`;
    const count = parseInt(localStorage.getItem(key) || '0', 10) + 1;
    localStorage.setItem(key, String(count));
    if (Math.random() < 0.05) {
      addLog(WISDOM_LINES[Math.floor(Math.random() * WISDOM_LINES.length)], '#a78bfa');
    }
    if (count === 100) {
      apiFetch<{ beans: number; alreadyClaimed: boolean }>('/api/gamification', { action: 'egg-claim', egg: 'tap_100' })
        .then((r) => {
          if (r && !r.alreadyClaimed) {
            setEggBeans((prev) => prev + r.beans);
            addLog('THE 100TH TAP. +25 BEANS. PERSISTENCE NOTED.', '#ffff00');
          }
        })
        .catch(() => { /* non-fatal */ });
    }
  }, [userAddress, addLog]);

  useKonami(
    useCallback(() => {
      document.body.classList.add('crt-glitch');
      setTimeout(() => document.body.classList.remove('crt-glitch'), 1200);
      setKonamiUnlocked(true);
      apiFetch('/api/gamification', { action: 'egg-claim', egg: 'konami' })
        .then((r: any) => {
          if (r && !r.alreadyClaimed) {
            setEggBeans((prev) => prev + r.beans);
          }
          addLog('THE OLD CODE STILL WORKS.', '#ff00ea');
        })
        .catch(() => { /* non-fatal */ });
    }, [addLog])
  );


  const handleResetLogin = useCallback(async () => {
    try {
      // Full nuke: clear auth + restart OAuth. logout() also clears Supabase JWT
      // and the per-user selectedShop keys; startGoogleLogin redirects out.
      logout();
      await startGoogleLogin();
    } catch (err) {
      console.error('[Dashboard] Reset login failed:', err);
      addLog('LOGIN RESET FAILED — RELOAD THE PAGE', '#e52222');
    }
  }, [logout, addLog]);

  // ── Activity log persistence ──
  // Transient error lines (red) and stale entries (>24h) shouldn't replay on
  // reload — they confuse the user into thinking something is broken right now
  // when it was just an old hiccup.
  const ERROR_COLOR = '#e52222';
  const LOG_TTL_MS = 24 * 60 * 60 * 1000;
  const isPersistableLog = (entry: LogEntry) =>
    entry.color !== ERROR_COLOR &&
    Date.now() - (entry.timestamp || 0) < LOG_TTL_MS;

  useEffect(() => {
    if (!userAddress) return;
    const storedLogs = localStorage.getItem(LOG_STORAGE_PREFIX + userAddress);
    if (storedLogs) {
      try {
        const parsed: LogEntry[] = JSON.parse(storedLogs);
        const filtered = parsed.filter(isPersistableLog);
        setLogs(filtered);
        logCounter.current = filtered.length;
      } catch { /* ignore */ }
    }
  }, [userAddress]);

  useEffect(() => {
    if (!userAddress || logs.length === 0) return;
    const toPersist = logs.filter(isPersistableLog).slice(0, 12);
    localStorage.setItem(LOG_STORAGE_PREFIX + userAddress, JSON.stringify(toPersist));
  }, [logs, userAddress]);

  // ── Load purchased lore from localStorage ──
  useEffect(() => {
    if (!userAddress) return;
    const stored = localStorage.getItem(PURCHASED_LORE_PREFIX + userAddress);
    if (stored) {
      try { setPurchasedLore(JSON.parse(stored)); } catch { /* ignore */ }
    }
  }, [userAddress]);

  // ── Quest completion celebration — fires the toast + audit write once when a
  // quest newly completes. Rewards themselves are derived above (not mutated
  // here). Seeded after the first load so pre-existing completions don't spam
  // the log on refresh. A quest that later un-completes (on-chain reset) is
  // removed from the seen set, so re-earning it celebrates again.
  const celebratedQuestsRef = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (loading) return;
    const seen = celebratedQuestsRef.current;
    if (seen === null) {
      celebratedQuestsRef.current = new Set(completedQuestIds);
      return;
    }
    // Drop any quests that are no longer complete so they can re-celebrate.
    for (const id of Array.from(seen)) {
      if (!completedQuestIds.includes(id)) seen.delete(id);
    }
    completedQuestIds.forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      const quest = QUESTS.find((q) => q.id === id);
      if (!quest) return;
      addLog(`QUEST COMPLETE: ${quest.name}! +${quest.reward} ${shopCurrency}`, '#feca57');
      // Idempotent via UNIQUE(customer_address, quest_id).
      completeQuest(quest.id, quest.reward).catch(() => { /* non-fatal */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, completedQuestIds.join(',')]);

  // ── Preemptive session-health check ──
  // If the user is "authenticated" (has JWT/address) but canSign() is false, the
  // session is broken. Show the recovery banner immediately instead of waiting
  // for them to tap the mug and hit an error.
  useEffect(() => {
    if (!userAddress) return;
    if (canSign()) return;
    console.warn('[Dashboard] Authenticated but cannot sign:', getSignDiagnostic());
    setSessionBroken(true);
  }, [userAddress]);

  // ── Data loading ──
  useEffect(() => {
    if (!userAddress) return;
    loadData();
  }, [userAddress]);

  // ── Avatar chip (Task 2) ── seeds the header chip's avatarUrl once the
  // customer's authenticated; swallow errors so the chip just falls back
  // to initials rather than blocking the rest of the dashboard.
  useEffect(() => {
    if (!userAddress) return;
    apiFetch<{ avatarUrl: string | null }>('/api/account', { action: 'profile-get' })
      .then((profile) => setAvatarUrl(profile?.avatarUrl ?? null))
      .catch(() => { /* chip falls back to initials */ });
  }, [userAddress]);

  async function loadData() {
    setLoading(true);
    try {
      // allSettled, not all: these five hit the public fullnode, and one 429/
      // timeout must not blank the whole dashboard (it used to render as
      // "your account vanished"). Profile is the only load-bearing read —
      // if IT failed, rethrow so the catch shows SYNC FAILED rather than
      // auto-registering a duplicate profile over a transient error.
      const settled = await Promise.allSettled([
        fetchCustomerProfile(userAddress!),
        fetchCustomerShopAccounts(userAddress!),
        fetchPurchaseHistory(userAddress!),
        fetchRewardNFTs(userAddress!),
        fetchShopCapabilities(userAddress!),
      ]);
      if (settled[0].status === 'rejected') throw settled[0].reason;
      const profileRaw = settled[0].value;
      const accountsRaw = settled[1].status === 'fulfilled' ? settled[1].value : [];
      const purchasesRaw = settled[2].status === 'fulfilled' ? settled[2].value : [];
      const nftsRaw = settled[3].status === 'fulfilled' ? settled[3].value : [];
      const shopCaps = settled[4].status === 'fulfilled' ? settled[4].value : [];
      settled.slice(1).forEach((s, i) => {
        if (s.status === 'rejected') {
          console.warn(`[loadData] partial fetch ${i + 1} failed:`, s.reason?.message || s.reason);
        }
      });

      // Check if user owns a shop (has ShopCapability on-chain). Persist it
      // per-address: this fetch is in the tolerated-failure group above, so a
      // transient RPC fault must not silently demote an owner back into the
      // cold-start view.
      const ownsShop = shopCaps.length > 0;
      setHasOnChainShop(ownsShop);
      if (ownsShop) {
        try { localStorage.setItem(shopOwnerStorageKey(userAddress), '1'); } catch { /* ignore */ }
      }

      // Parse global profile
      if (profileRaw) {
        const pf = (profileRaw as any).data?.content?.fields || {};
        setCustomerProfile({
          id: (profileRaw as any).data?.objectId,
          total_xp: parseInt(pf.total_xp) || 0,
          level: parseInt(pf.level) || 1,
          total_purchases: parseInt(pf.total_purchases) || 0,
        });
        prevLevel.current = parseInt(pf.level) || 1;
      } else {
        // No on-chain profile — auto-register customer
        if (!canSign()) {
          const reason = getSignDiagnostic();
          console.error('Cannot auto-register: canSign() = false:', reason);
          addLog('SIGN-IN INCOMPLETE — LOG OUT & LOG IN AGAIN', '#e52222');
        } else {
          try {
            addLog('REGISTERING YOUR PROFILE...', '#00ffff');
            const tx = buildRegisterCustomerTx('');
            await signAndExecuteTransaction(tx);
            addLog('PROFILE REGISTERED!', '#3dba4c');

            // If they arrived via a referral link, record it on-chain. The 20-pt
            // bonus stays locked until this customer buys 3 coffees (contract-enforced).
            try {
              const ref = localStorage.getItem('bitbrew_referral_ref');
              if (ref && ref.startsWith('0x') && !addressesEqual(ref, userAddress)) {
                await signAndExecuteTransaction(buildCreateReferralTx(ref, userAddress!, 20, 3));
                addLog('REFERRAL LINKED — BONUS UNLOCKS AFTER 3 COFFEES', '#ff00ea');
              }
              localStorage.removeItem('bitbrew_referral_ref');
            } catch (e) {
              console.warn('[referral] link failed', e);
            }
            // Re-fetch profile after registration
            const newProfile = await fetchCustomerProfile(userAddress!);
            if (newProfile) {
              const pf = (newProfile as any).data?.content?.fields || {};
              setCustomerProfile({
                id: (newProfile as any).data?.objectId,
                total_xp: parseInt(pf.total_xp) || 0,
                level: parseInt(pf.level) || 1,
                total_purchases: parseInt(pf.total_purchases) || 0,
              });
              prevLevel.current = parseInt(pf.level) || 1;
            }
          } catch (regErr: any) {
            console.error('Auto-registration failed:', regErr);
            addLog(`REGISTRATION FAILED: ${regErr?.message?.slice(0, 80) || 'UNKNOWN ERROR'}`, '#e52222');
          }
        }
      }

      // Parse shop accounts first to know which shops to fetch
      const parsedAccountsRaw = accountsRaw.map((obj: any) => {
        const f = obj.data?.content?.fields || {};
        return {
          id: obj.data?.objectId,
          shop: f.shop,
          points: parseInt(f.points) || 0,
          purchases: parseInt(f.purchases) || 0,
          shopObjectId: f.shop_id || '', // on-chain shop object ID if stored
        };
      });

      // Fetch shops the user has accounts for (private discovery) PLUS the user's
      // own shop if they own one — otherwise a freshly-registered owner-shop is
      // invisible until they also become a customer of it.
      const ownerSet = new Set(parsedAccountsRaw.map(a => a.shop).filter(Boolean) as string[]);
      if (userAddress) ownerSet.add(userAddress);
      const uniqueShopOwners = [...ownerSet];
      const shopLookups = await Promise.allSettled(
        uniqueShopOwners.map(async (ownerAddr) => {
          const shop = await getShopByOwner(ownerAddr);
          if (shop) return shop;
          // Skip placeholder rows for the current user when no shop exists yet
          if (addressesEqual(ownerAddr, userAddress)) return null;
          // Stand-in for a shop the customer has an on-chain account at, but whose
          // Supabase row hasn't loaded. name:null → renders as "Unnamed Shop" via
          // resolveShopName. `synthetic` flags it so the backfill below NEVER
          // persists it — fabricating an address-named row here was the original
          // bug that wrote "Shop 0x..." into shops.name permanently.
          return { owner_address: ownerAddr, owner: ownerAddr, name: null, synthetic: true };
        })
      );
      let parsedShops = shopLookups
        .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
        .map(r => r.value)
        .filter((s: any) => s != null);

      // Normalise owner field: Supabase returns owner_address, add owner alias for compatibility
      parsedShops = parsedShops.map((s: any) => ({
        ...s,
        owner: s.owner_address || s.owner,
      }));

      // NOTE: onchain_id backfill used to live here as a client-side upsert —
      // it was the source of the address-as-name corruption (it wrote fabricated
      // placeholder names back to Supabase). The client no longer writes shop
      // identity. The scheduled server-side reconcile ("librarian", api/shops
      // action:'reconcile') keeps onchain_id in sync from chain, and the stamp
      // flow resolves a missing onchain_id on-demand via findShopIdByOwner.

      // Enrich accounts with shop names. resolveShopName never returns an address —
      // a missing/synthetic shop renders as "Unnamed Shop", not "Shop 0x...".
      const parsedAccounts = parsedAccountsRaw.map((acct) => {
        const shop = parsedShops.find((s: any) => addressesEqual(s.owner, acct.shop));
        return {
          ...acct,
          shopName: resolveShopName(shop),
          shopId: shop?.id,
        };
      });

      setAllShops(parsedShops);
      setShopAccounts(parsedAccounts);
      if (parsedAccounts.length > 0 && !selectedShop) {
        const saved = localStorage.getItem(selectedShopKey(userAddress));
        const validSaved = saved && parsedAccounts.find((a: any) => addressesEqual(a.shop, saved));
        setSelectedShop(validSaved ? saved : parsedAccounts[0].shop);
      }

      // Parse purchases
      const parsedPurchases = purchasesRaw
        .map((obj: any) => {
          const f = (obj.data?.content as any)?.fields || {};
          return {
            id: obj.data?.objectId,
            coffeeShop: f.coffee_shop,
            pointsEarned: parseInt(f.points_earned) || 10,
            timestamp: parseInt(f.timestamp) || 0,
          };
        })
        .sort((a: any, b: any) => b.timestamp - a.timestamp);
      setPurchases(parsedPurchases);

      // Parse reward NFTs
      const parsedNFTs = nftsRaw.map((obj: any) => {
        const f = obj.data?.content?.fields || {};
        const shop = parsedShops.find((s: any) => addressesEqual(s.owner, f.shop));
        return {
          id: obj.data?.objectId,
          customer: f.customer,
          shop: f.shop,
          shopName: resolveShopName(shop),
          level_reached: parseInt(f.level_reached) || 1,
          timestamp: parseInt(f.timestamp) || 0,
        };
      });

      setRewardNFTs(parsedNFTs);
      setLastSyncedAt(Date.now());
    } catch (err) {
      console.error('Dashboard load failed:', err);
      addLog('SYNC FAILED — CHECK CONNECTION', '#e52222');
    }
    setLoading(false);
  }

  // ── Supabase Realtime subscription (customer role) ──
  // Wait for the Supabase JWT to be restored before subscribing — otherwise
  // the channel can attach without auth and the RLS-gated subscription
  // silently drops events.
  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    (async () => {
      await restoreSupabaseSession(userAddress);
      if (cancelled) return;
      realtimeClient.subscribeAsCustomer(
        userAddress,
        (row: StampRequestRow) => { setLatestStampUpdate(row); },
        (row: RedeemRequestRow) => { setLatestRedeemUpdate(row); },
      );
    })();
    return () => { cancelled = true; realtimeClient.cleanup(); };
  }, [userAddress]);

  // ── Refresh shops when navigated from a register/owner action ──
  useEffect(() => {
    const state = location.state as { refreshShops?: boolean } | null;
    if (state?.refreshShops && userAddress) {
      loadData();
      // Clear the flag so the effect doesn't re-fire on a tab change.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, userAddress]);

  // ── Auto-recovery: drive any approved-but-unconfirmed stamps to on-chain ──
  // If the customer was offline / refreshed when the barista approved, the
  // realtime event was missed. Three stuck shapes, three treatments:
  //   'none'       → missed approval; feed through the realtime handler.
  //   'failed'     → earlier sign attempt errored; retry directly, ONCE per
  //                  mount. (Must NOT go through the realtime handler — our
  //                  own 'failed' status write echoes back as a realtime
  //                  event, and retrying from there loops forever.)
  //   'submitting' → ambiguous: tx may be in flight in another tab. Only
  //                  retry when stale (>10 min), same once-per-mount guard.
  useEffect(() => {
    if (!userAddress) return;
    getStuckStamps(userAddress).then(stuck => {
      if (stuck.length === 0) return;
      addLog(`RECOVERING ${stuck.length} UNCLAIMED STAMP${stuck.length > 1 ? 'S' : ''}...`, '#22d3ee');
      stuck.forEach(row => {
        if (row.onchain_status === 'none') {
          setLatestStampUpdate(row);
          return;
        }
        const rowAge = Date.now() - new Date(row.responded_at || row.created_at).getTime();
        const retryable =
          row.onchain_status === 'failed' ||
          (row.onchain_status === 'submitting' && rowAge > 10 * 60_000);
        if (retryable && !recoveryAttempted.current.has(row.id)) {
          recoveryAttempted.current.add(row.id);
          recordOnChain(row);
        }
      });
    });
  }, [userAddress]);

  // ── Helper: add a newly-discovered shop to state + create on-chain account ──
  // Returns true on success (account created or already existed), false on failure.
  // Callers should use the return value to decide whether to keep the selectedShop
  // switch or roll it back.
  async function addNewShopToState(shop: any): Promise<boolean> {
    setAllShops(prev => {
      if (prev.find((s: any) => addressesEqual(s.owner, shop.owner))) return prev;
      // Keep the FULL row — PixelPass reads stamp_icon/pixel_image_url from
      // allShops; truncating here made first-visit shops render default stamps.
      return [...prev, { ...shop, owner: shop.owner || shop.owner_address }];
    });
    // Skip if we already have an account for this shop. Still confirm to the
    // customer that they're connected — this function now owns that message on
    // every success path, so callers must not pre-announce it.
    if (shopAccounts.find((a: any) => addressesEqual(a.shop, shop.owner))) {
      addLog(`CONNECTED TO: ${resolveShopName(shop)}`, '#3dba4c');
      return true;
    }
    // Create on-chain CustomerShopAccount
    if (userAddress && customerProfile?.id) {
      try {
        addLog('JOINING SHOP...', '#00ffff');
        const tx = buildCreateShopAccountTx(customerProfile.id, shop.owner);
        await signAndExecuteTransaction(tx);
      } catch (err: any) {
        console.error('create_shop_account threw:', err);
        if (isSessionError(err)) {
          setSessionBroken(true);
          return false;
        }
        // A throw here is NOT proof the write failed. executeTransactionBlock
        // can reject on the RESPONSE after the node already executed the
        // transaction, and on 2026-08-02 that told a customer their join had
        // failed while they demonstrably owned the shop account. Ask the chain
        // before saying anything to them.
        const confirmation = await confirmShopAccount(userAddress, shop.owner);
        if (confirmation === 'absent') {
          addLog('COULD NOT JOIN SHOP — TAP THE SHOP TO RETRY', '#e52222');
          return false;
        }
        if (confirmation === 'unknown') {
          // We could not verify either way. Do NOT roll the selection back:
          // discarding a join that may well have succeeded is the exact
          // failure being fixed, and a retry is cheap where a lost shop is not.
          addLog('JOIN UNCONFIRMED — CHECK CONNECTION AND REFRESH', '#e5a122');
          return true;
        }
        // 'exists' — it landed after all.
      }

      addLog(`CONNECTED TO: ${resolveShopName(shop)}`, '#3dba4c');
      // loadData() is a READ and lives OUTSIDE the write's error handling. It
      // used to sit inside the try above, so a flaky chain read was reported as
      // "account creation failed" and rolled back a join that had succeeded.
      try {
        await loadData();
      } catch (refreshErr) {
        console.error('Post-join refresh failed (the join itself is fine):', refreshErr);
      }
      return true;
    }
    // No user yet — treat as soft-success; nothing to roll back.
    return true;
  }

  // ── Self-heal: selected shop with no on-chain account ──
  // If a shop was joined before the profile finished loading (deep-link race),
  // addNewShopToState soft-succeeded WITHOUT creating the CustomerShopAccount,
  // and every later select treats the shop as "known" — a permanent dead-end
  // ("NO AREA SELECTED" forever). Whenever the selected shop lacks an account
  // and the profile is ready, create it. Once per shop per mount so a failing
  // tx can't spin.
  const accountHealAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (loading || !selectedShop || !userAddress || !customerProfile?.id) return;
    if (shopAccounts.find((a: any) => addressesEqual(a.shop, selectedShop))) return;
    const shop = allShops.find((s: any) => addressesEqual(s.owner, selectedShop));
    if (!shop) return;
    const key = normalizeAddress(selectedShop);
    if (accountHealAttempted.current.has(key)) return;
    accountHealAttempted.current.add(key);
    addNewShopToState(shop);
  }, [loading, selectedShop, customerProfile?.id, shopAccounts.length, allShops.length]);

  // ── QR deep link: auto-select shop from URL param ──
  useEffect(() => {
    const shopFromUrl = searchParams.get('shop');
    if (!shopFromUrl || loading) return;

    const matchedShop = allShops.find((s: any) => addressesEqual(s.owner, shopFromUrl));
    if (matchedShop) {
      setSelectedShop(matchedShop.owner);
      addLog(`CONNECTED TO: ${resolveShopName(matchedShop)}`, '#00ffff');
      setSearchParams({}, { replace: true });
    } else {
      // Try fetching from Supabase — shop may exist but wasn't in initial load.
      // Capture previous shop so we can roll back if account creation fails.
      const previousShop = selectedShop;
      getShopByOwner(shopFromUrl)
        .then(async shop => {
          if (shop) {
            const shopWithOwner = { ...shop, owner: shop.owner_address };
            setSelectedShop(shop.owner_address);
            // No CONNECTED TO here — announcing it before the join has landed
            // is what let a failed join still read as a success, and
            // addNewShopToState now logs it on every path that actually works.
            const ok = await addNewShopToState(shopWithOwner);
            if (!ok) {
              setSelectedShop(previousShop);
              if (previousShop) addLog('STAYED ON YOUR PREVIOUS SHOP', '#e5a122');
            }
          } else {
            addLog('SCANNED SHOP NOT FOUND', '#e52222');
          }
        })
        .catch(() => {
          addLog('SCANNED SHOP NOT FOUND', '#e52222');
        });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, loading, allShops]);

  // ── Level-up detection ──
  useEffect(() => {
    if (prevLevel.current > 0 && globalLevel > prevLevel.current) {
      setShowLevelUp(true);
      addLog(`LEVEL UP! NOW LEVEL ${globalLevel}!`, '#00ffff');
      addLog('ITEM BOX UNLOCKED — CHECK LOOT BAG', '#ff00ea');
      setTimeout(() => setShowLevelUp(false), 3000); // > the 2.8s .level-up-flash animation
    }
    prevLevel.current = globalLevel;
  }, [globalLevel, addLog]);

  // ── First-stamp reveal ──
  // recordOnChainInner dispatches `bitbrew:first-stamp` exactly once, the moment
  // the very first stamp confirms. Flash the welcome beat; the loadData() that
  // follows flips total_purchases to 1, so the cold-start gate closes and the
  // full dashboard mounts underneath.
  useEffect(() => {
    const onFirstStamp = () => {
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 3000); // > the 2.8s .level-up-flash animation
    };
    const onStampGet = (e: Event) => {
      const qty = (e as CustomEvent).detail?.quantity || 1;
      setStampGet({ show: true, quantity: qty });
      setTimeout(() => setStampGet({ show: false, quantity: qty }), 3000);
    };
    window.addEventListener('bitbrew:first-stamp', onFirstStamp);
    window.addEventListener('bitbrew:stamp-get', onStampGet);
    return () => {
      window.removeEventListener('bitbrew:first-stamp', onFirstStamp);
      window.removeEventListener('bitbrew:stamp-get', onStampGet);
    };
  }, []);

  // ── Helper: customer signs record_purchase on-chain after barista approval ──
  // Requests currently being recorded — the instant-sign path (Square match
  // response) and the realtime UPDATE echo can both arrive before the first
  // status write lands, double-firing the same tx (chain rejects the
  // duplicate as equivocation, but the user sees a scary RECORD FAILED).
  const recordingInFlight = useRef<Set<string>>(new Set());

  async function recordOnChain(row: StampRequestRow) {
    if (recordingInFlight.current.has(row.id)) return;
    recordingInFlight.current.add(row.id);
    try {
      await recordOnChainInner(row);
    } finally {
      recordingInFlight.current.delete(row.id);
    }
  }

  async function recordOnChainInner(row: StampRequestRow) {
    const reqId = row.id;
    const quantity = row.quantity || 1;
    console.log('[recordOnChain] Starting with row:', { id: reqId, quantity, shopAccountId: row.customer_shop_account_id, profileId: row.customer_profile_id, shopOwner: row.shop_owner_address });

    if (!row.customer_shop_account_id || !row.customer_profile_id) {
      if (!userAddress) {
        addLog('MISSING ACCOUNT DATA — REFRESH & RETRY', '#e52222');
        loadData();
        return;
      }
      addLog('LINKING YOUR CARD...', '#00ffff');
      const ids = await resolveStampIds(row, userAddress, defaultResolverDeps);
      if (!ids) { addLog('MISSING ACCOUNT DATA — REFRESH & RETRY', '#e52222'); loadData(); return; }
      row = { ...row, customer_shop_account_id: ids.accountId, customer_profile_id: ids.profileId };
    }

    // Resolve on-chain CoffeeShop object ID. `s.id` is the Supabase UUID, NOT a
    // Sui address — only `onchain_id` is safe to pass to a Move call. Fall back
    // to live event lookup if the Supabase row hasn't captured onchain_id yet.
    const shopRow = allShops.find((s: any) =>
      addressesEqual(s.owner, row.shop_owner_address) || addressesEqual(s.owner_address, row.shop_owner_address)
    );
    let resolvedShopId: string | null = shopRow?.onchain_id || null;
    if (!resolvedShopId) {
      try {
        resolvedShopId = await findShopIdByOwner(row.shop_owner_address);
      } catch { /* fall through to error */ }
    }
    console.log('[recordOnChain] Resolved shopId:', resolvedShopId, '| shopRow:', shopRow);

    if (!resolvedShopId) {
      addLog('SHOP NOT FOUND — REFRESH & RETRY', '#e52222');
      loadData();
      return;
    }

    try {
      await updateStampOnchainStatus(reqId, 'submitting');
      addLog(`RECORDING ${quantity > 1 ? `${quantity}x ` : ''}ON-CHAIN...`, '#00ffff');
      console.log('[recordOnChain] Building tx with:', { account: row.customer_shop_account_id, profile: row.customer_profile_id, shop: resolvedShopId, quantity });
      // Non-null: the guard above (resolve-or-return) already ensured both ids
      // are present before this point — TS can't track that across the `row =
      // {...}` reassignment inside the guard, so assert what we've verified.
      // buildStampOnChainTx picks the attested path when this row carries a
      // server attestation and the network's StampAuthority is configured,
      // falling back to legacy record_purchase/record_batch_purchases
      // otherwise (contracts/STAMP_AUTHORIZATION_DESIGN.md).
      const tx = buildStampOnChainTx(
        row, row.customer_shop_account_id!, row.customer_profile_id!, resolvedShopId, quantity,
      );
      const result = await signAndExecuteTransaction(tx);
      const txDigest = (result as any)?.digest;
      await updateStampOnchainStatus(reqId, 'confirmed', txDigest);
      console.log('[recordOnChain] SUCCESS:', result);
      addLog(`STAMP RECORDED! +${quantity * 10} ${shopCurrency}`, '#3dba4c');
      // Start cooldown timer
      if (userAddress && row.shop_owner_address) {
        recordStampCooldown(userAddress, row.shop_owner_address);
      }
      // Which reveal: WELCOME only for the first stamp EVER — decided against
      // on-chain total_purchases (server truth), not just the device flag,
      // because cleared storage / a new device replayed the welcome at
      // long-time customers. Every other stamp gets the STAMP GET! beat.
      const firstStampKey = firstStampStorageKey(userAddress);
      const firstEver = isFirstStampEver(
        customerProfile?.total_purchases || 0,
        !!localStorage.getItem(firstStampKey),
      );
      // Flags always set: per-user gates cold-start; the device-global one
      // unlocks InstallPrompt's existing check — both regardless of reveal.
      if (!localStorage.getItem(firstStampKey)) {
        localStorage.setItem(firstStampKey, '1');
        localStorage.setItem('bitbrew_first_stamp_done', '1');
      }
      if (firstEver) {
        window.dispatchEvent(new Event('bitbrew:first-stamp'));
      } else {
        window.dispatchEvent(new CustomEvent('bitbrew:stamp-get', { detail: { quantity } }));
      }
    } catch (err: any) {
      console.error('[recordOnChain] FAILED:', err?.message || err);
      await updateStampOnchainStatus(reqId, 'failed');
      if (isSessionError(err)) {
        setSessionBroken(true);
      } else {
        addLog(`RECORD FAILED: ${err?.message?.slice(0, 60) || 'UNKNOWN'}`, '#e52222');
      }
    }
    loadData();
  }

  // ── Stamp request lifecycle (Supabase Realtime) ──
  // Drive UI from the row's status/onchain_status fields, not from an in-memory
  // ref — this works across tabs, page reloads, and stamps initiated by other
  // devices.
  useEffect(() => {
    if (!latestStampUpdate) return;
    const row = latestStampUpdate;

    // Always clear "stamping" if this row is the one we initiated.
    if (activeRequestId.current === row.id && row.status !== 'pending') {
      activeRequestId.current = null;
      setStampingShop(null);
    }

    if (row.status === 'approved' && row.onchain_status === 'none') {
      // Run on-chain recording exactly once per row by gating on onchain_status.
      recordOnChain(row);
    } else if (row.status === 'denied') {
      addLog('STAMP DENIED BY BARISTA', '#e52222');
    } else if (row.status === 'expired') {
      addLog('REQUEST EXPIRED — TRY AGAIN', '#e52222');
    }
  }, [latestStampUpdate]);

  // ── Actions ──
  async function handleBrew() {
    if (activeRequestId.current || stampingShop) return;
    if (!userAddress) return;
    if (!customerProfile?.id) {
      addLog('NO PROFILE — LOG OUT & LOG IN AGAIN', '#e52222');
      return;
    }
    if (!currentAccount) {
      addLog('NO SHOP ACCOUNT — SELECT A SHOP FIRST', '#e52222');
      bumpIdleTaps();
      return;
    }
    if (!canSign()) {
      addLog('SESSION EXPIRED — LOG OUT & LOG IN AGAIN', '#e52222');
      return;
    }

    // Cooldown check — matches on-chain 5-minute E_PURCHASE_TOO_SOON
    const cooldown = getStampCooldownRemaining(userAddress, currentAccount.shop);
    if (cooldown > 0) {
      const mins = Math.ceil(cooldown / 60000);
      addLog(`COOLDOWN: WAIT ${mins} MIN BEFORE NEXT STAMP`, '#e52222');
      bumpIdleTaps();
      return;
    }

    // Resolve Supabase shop UUID for the stamp_requests FK
    const supabaseShop = allShops.find((s: any) => addressesEqual(s.owner, currentAccount.shop));
    const supabaseShopId = supabaseShop?.supabase_id || supabaseShop?.id;
    if (!supabaseShopId) {
      addLog('SHOP NOT SYNCED — REFRESHING...', '#e52222');
      loadData();
      return;
    }


    console.log('[handleBrew] Creating stamp request:', { supabaseShopId, shopOwner: currentAccount.shop, accountId: currentAccount.id, profileId: customerProfile.id });
    setStampingShop(currentAccount.shop);

    const req = await createStampRequest({
      customerAddress: userAddress,
      shopId: supabaseShopId,
      shopOwnerAddress: currentAccount.shop,
      customerShopAccountId: currentAccount.id,
      customerProfileId: customerProfile.id,
    });

    if (!req) {
      setStampingShop(null);
      addLog('FAILED TO SEND REQUEST', '#e52222');
      return;
    }

    activeRequestId.current = req.id;
    console.log('[handleBrew] Request created:', req.id);

    // Square shops: the till payment replaces barista approval.
    const sq = req as any;
    if (sq.squareMatched) {
      // Unambiguous payment match — already approved; sign immediately.
      addLog('TILL PAYMENT MATCHED — RECORDING...', '#3dba4c');
      setLatestStampUpdate(req);
      return;
    }
    if (Array.isArray(sq.squareCandidates) && sq.squareCandidates.length > 0) {
      // Several unclaimed payments — ask which one was theirs.
      setSquarePick({ requestId: req.id, candidates: sq.squareCandidates });
      addLog('WHICH PAYMENT WAS YOURS?', '#facc15');
    } else if (sq.squarePending) {
      // Tap-then-pay: webhook auto-approves when the payment lands.
      addLog('WAITING FOR TILL PAYMENT...', '#facc15');
    } else {
      addLog(`STAMP REQUESTED: ${currentAccount.shopName}`, '#ff00ea');
    }

    setTimeout(() => {
      // Only show expired if this request is still the active one and wasn't approved
      if (activeRequestId.current === req.id) {
        activeRequestId.current = null;
        setStampingShop(null);
        addLog('REQUEST EXPIRED — TRY AGAIN', '#e52222');
      }
    }, 120000); // 2 minutes — matches stamp-request expiry
  }

  // Customer resolves an ambiguous Square match by claiming one payment.
  async function handleSquarePick(paymentId: string) {
    if (!squarePick) return;
    const claimed = await claimSquarePayment(squarePick.requestId, paymentId);
    if (claimed) {
      setSquarePick(null);
      addLog('PAYMENT CLAIMED — RECORDING...', '#3dba4c');
      setLatestStampUpdate(claimed);
    } else {
      // Raced another customer — drop that candidate, keep the picker open.
      setSquarePick(prev => prev && ({
        ...prev,
        candidates: prev.candidates.filter(c => c.id !== paymentId),
      }));
      addLog('PAYMENT ALREADY CLAIMED — PICK ANOTHER', '#e52222');
    }
  }

  async function handleRedeemNFT(nft: any) {
    if (!userAddress) return;

    // Resolve the Supabase shop UUID for the redeem_requests FK
    const supabaseShop = allShops.find((s: any) => addressesEqual(s.owner, nft.shop));
    const supabaseShopId = supabaseShop?.supabase_id || supabaseShop?.id;
    if (!supabaseShopId) {
      addLog('SHOP NOT SYNCED — REFRESH & RETRY', '#e52222');
      return;
    }

    // No request, no queue, no waiting. The reward opens at the counter and
    // the BARISTA's tap is what burns it — see RewardCard and
    // docs/superpowers/specs/2026-07-29-reward-redemption-at-counter-design.md.
    // The old flow created a pending row and told the customer to wait for an
    // approval nobody works during service.
    setReveal(null); // clear any stale reveal from a previous redemption
    counterRevealRequestId.current = null; // no tap yet — nothing active to reveal for
    setCounterRedeem({
      nftId: nft.id,
      shopId: supabaseShopId,
      shopName: resolveShopName(supabaseShop) || 'THIS SHOP',
      logoUrl: supabaseShop?.pixel_image_url || supabaseShop?.image_url || null,
      itemTitle: supabaseShop?.reward_label || null,
    });
  }

  // Burn the approved reward NFT → voucher. Shared by the realtime path and
  // the stuck-redeem recovery below.
  async function burnRedeemOnChain(row: RedeemRequestRow) {
    // One burn per redeem row, whichever trigger gets here first. Three do:
    // the barista's tap (onBurned), the customer realtime channel — which
    // subscribes with event '*', so the tap's own INSERT arrives as
    // status='approved' + onchain_status='none' and matches the burn
    // condition — and stuck-recovery. Unguarded, the tap and the echo of its
    // own insert both sign a burn of the same NFT: one wins, the loser throws,
    // overwrites the winner's 'confirmed' with 'failed', and tells the
    // customer BURN FAILED for a coffee they were actually given.
    if (burnAttempted.current.has(row.id)) return;
    burnAttempted.current.add(row.id);
    setPendingRedeemNftIds(prev => prev.filter(id => id !== row.nft_id));
    try {
      await updateRedeemOnchainStatus(row.id, 'submitting');
      addLog('BURNING ITEM BOX ON-CHAIN...', '#00ffff');
      const tx = buildRedeemRewardNFTTx(row.nft_id);
      const result = await signAndExecuteTransaction(tx);
      const txDigest = (result as any)?.digest;
      await updateRedeemOnchainStatus(row.id, 'confirmed', txDigest);
      setRewardNFTs(prev => prev.filter(n => n.id !== row.nft_id));
      addLog('ITEM BOX BURNED! VOUCHER MINTED', '#3dba4c');
      // The award trigger ran inside that same update's transaction, so the
      // row exists now. resolveConfirmedReveal isolates the lookup from this
      // try/catch and guards it against staleness — see its doc comment. No
      // row, a lookup error, or a stale resolution all mean: no reveal.
      const awarded = await resolveConfirmedReveal(row.id, () => counterRevealRequestId.current === row.id);
      if (awarded) setReveal(awarded);
    } catch (err: any) {
      console.error('Failed to burn NFT:', err);
      await updateRedeemOnchainStatus(row.id, 'failed');
      if (isSessionError(err)) {
        setSessionBroken(true);
      } else {
        addLog('BURN FAILED — TRY AGAIN', '#e52222');
      }
    }
  }

  // ── Redeem approval detection (Supabase Realtime) ──
  useEffect(() => {
    if (!latestRedeemUpdate) return;
    const row = latestRedeemUpdate;

    // Gate on onchain_status: our own 'submitting'/'confirmed'/'failed'
    // writes echo back as realtime UPDATEs with status still 'approved' —
    // without the gate each echo re-triggered the burn.
    if (row.status === 'approved' && row.onchain_status === 'none') {
      burnRedeemOnChain(row);
    } else if (row.status === 'denied') {
      setPendingRedeemNftIds(prev => prev.filter(id => id !== row.nft_id));
      addLog('REDEEM DENIED BY SHOP', '#e52222');
    } else if (row.status === 'expired') {
      setPendingRedeemNftIds(prev => prev.filter(id => id !== row.nft_id));
      addLog('REDEEM REQUEST EXPIRED — TRY AGAIN', '#e52222');
    }
  }, [latestRedeemUpdate]);

  // ── Auto-recovery: approved-but-unburned redeems (mirror of the stamp
  // recovery above; same once-per-mount guard, same stale rule) ──
  useEffect(() => {
    if (!userAddress) return;
    getStuckRedeems(userAddress).then(stuck => {
      if (stuck.length === 0) return;
      addLog(`RECOVERING ${stuck.length} UNCLAIMED REDEEM${stuck.length > 1 ? 'S' : ''}...`, '#22d3ee');
      stuck.forEach(row => {
        if (recoveryAttempted.current.has(row.id)) return;
        const rowAge = Date.now() - new Date(row.responded_at || row.created_at).getTime();
        const retryable =
          row.onchain_status === 'none' ||
          row.onchain_status === 'failed' ||
          (row.onchain_status === 'submitting' && rowAge > 10 * 60_000);
        if (retryable) {
          recoveryAttempted.current.add(row.id);
          burnRedeemOnChain(row);
        }
      });
    });
  }, [userAddress]);

  // ── Shout-a-mate attribution (041): consume the pre-OAuth ?ref= stash once ──
  useEffect(() => {
    if (!userAddress) return;
    const ref = localStorage.getItem('bitbrew_referral_ref');
    if (!ref) return;
    localStorage.removeItem('bitbrew_referral_ref');
    if (!addressesEqual(ref, userAddress)) attributeReferral(ref);
  }, [userAddress]);

  // ── Own birthday (041): drives the birthday-week banner for perk shops ──
  useEffect(() => {
    if (!userAddress) return;
    let dead = false;
    apiFetch<{ birthdayMonth: number | null; birthdayDay: number | null }>('/api/account', { action: 'profile-get' })
      .then((r) => {
        if (!dead && r.birthdayMonth && r.birthdayDay) {
          setMyBirthday({ month: r.birthdayMonth, day: r.birthdayDay });
        }
      })
      .catch(() => { /* banner just doesn't show */ });
    return () => { dead = true; };
  }, [userAddress]);

  // ── Collection layer: catalog + owned + redemption count ──
  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    (async () => {
      const shopRow = allShops.find((s: any) => addressesEqual(s.owner, selectedShop));
      const shopUuid = shopRow?.supabase_id || shopRow?.id || null;
      const scopedShopUuid = typeof shopUuid === 'string' && shopUuid.includes('-') ? shopUuid : null;
      const [catalog, owned, count, egg, streakState, refBeans, sponsor, mints] = await Promise.all([
        getCatalog(scopedShopUuid),
        getMyCollectibles(userAddress),
        getRedemptionCount(userAddress),
        getEggBeans(userAddress),
        getMyStreakState(userAddress, scopedShopUuid),
        getReferralBeans(userAddress),
        getActiveSponsorFeature(),
        getMintCounts(),
      ]);
      if (cancelled) return;
      // Owned items must always render, even if their shop deactivated them
      // or the customer is currently viewing a different shop's catalog —
      // backfill any owned collectible_ids that fell outside the shop-scoped,
      // active-only `catalog` fetch.
      const missingIds = owned.map(o => o.collectible_id).filter(id => !catalog.some(c => c.id === id));
      const extras = missingIds.length > 0 ? await getCatalogByIds(missingIds) : [];
      if (cancelled) return;
      setCollection(mergeCollection([...catalog, ...extras], owned));
      setRedemptionCount(count);
      setEggBeans(egg);
      setShopStreak(streakState.streak);
      setStreakRepairable(streakState.repairableWeek);
      setStreakRepairSpent(streakState.repairSpentBeans);
      setReferralBeans(refBeans);
      setSponsorFeature(sponsor);
      setMintCounts(mints);
    })();
    return () => { cancelled = true; };
    // allShops.length: shop-UUID resolution reads allShops, which is still []
    // on the first run for returning customers — re-run once shops load.
  }, [userAddress, selectedShop, latestRedeemUpdate, allShops.length]);

  function handlePurchaseLore(loreId: string, cost: number) {
    if (availableFunBeans < cost) return;
    // Track purchased lore (gamification only — beans gate shown but not deducted on-chain)
    const updated = [...purchasedLore, loreId];
    setPurchasedLore(updated);
    if (userAddress) {
      localStorage.setItem(PURCHASED_LORE_PREFIX + userAddress, JSON.stringify(updated));
    }
    const lore = LORE_DATABASE.find(l => l.id === loreId);
    addLog(`LEGENDARY LORE ACQUIRED: ${lore?.title || loreId}`, '#feca57');
    // Persist to Supabase (idempotent via UNIQUE(customer_address, lore_id))
    purchaseLore(loreId, cost).catch(() => { /* non-fatal */ });
  }

  // ── Loading state ──
  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center min-h-[calc(100dvh-4rem)]">
          <div className="text-center">
            <div className="font-grotesk font-bold text-xl animate-pulse text-coral">Loading…</div>
            <div className="font-body text-xs mt-4 opacity-50">LOADING YOUR DATA</div>
          </div>
        </div>
        <WelcomeFlash show={showWelcome} />
        <StampFlash show={stampGet.show} quantity={stampGet.quantity} currencyLabel={shopCurrency} accentColor={shopAccent} />
      </>
    );
  }

  const isStamping = stampingShop === currentAccount?.shop;
  const isWaiting = isStamping;

  // Defined once and rendered in both the cold-start branch and the full
  // dashboard so the "find a shop" action works in either — no duplication.
  const shopModal = (
    <ShopModal
      show={showShopModal}
      onClose={() => setShowShopModal(false)}
      allShops={allShops}
      shopAccounts={shopAccounts}
      selectedShop={selectedShop}
      onSelectShop={(owner, name) => {
        setSelectedShop(owner);
        setShowShopModal(false);
        addLog(`SWITCHED SHOP: ${name}`, '#00ffff');
      }}
      onNewShopDiscovered={(shop) => {
        const previousShop = selectedShop;
        setSelectedShop(shop.owner);
        setShowShopModal(false);
        addNewShopToState(shop).then(ok => {
          if (!ok) {
            setSelectedShop(previousShop);
            // Only claim to have kept something if there WAS something. A
            // first-time customer has no previous shop, and telling them we
            // "kept" one while showing FIND A SHOP TO START is nonsense.
            if (previousShop) addLog('STAYED ON YOUR PREVIOUS SHOP', '#e5a122');
          }
        });
      }}
    />
  );

  // Same reasoning as shopModal: defined once so both the cold-start branch
  // and the full dashboard can reach the session-recovery banner and the
  // Square payment picker (handleBrew can trigger either from a first tap).
  const sessionBanner = sessionBroken ? <SessionRecoveryBanner onReset={handleResetLogin} /> : null;

  const squarePickModal = squarePick ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center safe-modal p-4 bg-[rgba(28,25,23,.55)]">
      <div className="w-full max-w-md bg-background pixel-corners p-6 sm:p-7 max-h-full overflow-y-auto">
        <p className="font-heading text-[8px] text-coral tracking-[1px] mb-2">WHICH PAYMENT WAS YOURS?</p>
        <p className="font-grotesk font-medium text-[13px] text-body-grey mb-4">
          A few till payments just came through — pick yours to claim the stamp.
        </p>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {squarePick.candidates.map(c => (
            <button
              key={c.id}
              onClick={() => handleSquarePick(c.id)}
              className="w-full bg-white pixel-corners font-grotesk font-bold text-[13px] text-primary flex justify-between px-4 py-3"
            >
              <span>
                {(c.amount_cents / 100).toLocaleString(undefined, { style: 'currency', currency: c.currency || 'AUD' })}
              </span>
              <span className="font-medium text-body-grey">
                {new Date(c.paid_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={() => { setSquarePick(null); addLog('OK — BARISTA CAN APPROVE MANUALLY', '#00ffff'); }}
          className="w-full pixel-corners-sm bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[12px] py-3 mt-4"
        >
          NONE OF THESE — WAIT FOR BARISTA
        </button>
      </div>
    </div>
  ) : null;

  // Same shape BrewStation always takes — computed once, above the cold-start
  // gate, so the locked and full renders pass the identical object.
  // allShops rows are Supabase SNAKE_CASE (getShopByOwner) — camelCase-only
  // reads here rendered the letter fallback forever.
  const brewStation = {
    shopName: currentAccount?.shopName || null,
    shopImageUrl: selectedShopRow?.image_url || selectedShopRow?.imageUrl,
    shopPixelImageUrl: selectedShopRow?.pixel_image_url || selectedShopRow?.pixelImageUrl,
    isWaiting: !!isWaiting,
    hasShop: !!selectedShop && !!currentAccount,
    accentColor: shopAccent,
    currencyLabel: shopCurrency,
    stampWindows: selectedShopRow?.stamp_windows,
    onBrew: handleBrew,
    onFindShop: () => setShowShopModal(true),
  };

  // ── TabShell header (Task 6) — shared by the locked and full renders so
  // level/title/chip stay consistent regardless of which tabs are reachable;
  // only xpPercent is hidden by TabShell itself when locked.
  const eyebrow = `LV.${globalLevel} · ${totalXP} / ${nextThreshold} XP`;
  const levelTitle = getLevelTitle(globalLevel);
  const statusLabel = (currentAccount?.shopName || 'BIT BREW').toUpperCase();
  // Avatar chip — tapping it jumps straight to the MORE tab (profile home),
  // the same selectTab instance StampCard.onOpenPass already uses.
  const displayName = getDisplayName();
  const initials = displayName
    ? displayName.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '☕';
  const chip = (
    <button
      onClick={() => selectTab('more')}
      aria-label="profile"
      className="flex-none w-[46px] h-[46px] pixel-corners bg-pixel-cyan flex items-center justify-center font-grotesk font-bold text-[15px] text-teal-ink overflow-hidden"
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="w-full h-full object-cover"
          style={{ imageRendering: 'pixelated' }}
        />
      ) : (
        initials
      )}
    </button>
  );

  // The cold-start gate is GONE (Nik, 2026-08-12). It locked every tab but
  // MORE for a customer with no stamps — a rule written for the old single
  // screen shell, where the panels genuinely had nowhere to put an empty
  // state. The tabbed design carries its own empty states, so the gate only
  // hid them.
  //
  // It was also a DEAD END: TabBar disabled every slot but MORE, HOME
  // included, so a first-visit customer who tapped MORE could never get back
  // to the mug — and the mug is the only way to request the first stamp that
  // would have unlocked it. The one escape was editing the URL.

  // Konami reward: a cosmetic secret lore card, appended only once unlocked —
  // unlockCheck always true since it's gated by client state, not checkData.
  const loreDbWithSecrets = konamiUnlocked
    ? [...effectiveLore, { ...SECRET_LORE, unlockCheck: () => true } as LoreEntry]
    : effectiveLore;
  const purchasedLoreIdsWithSecrets = konamiUnlocked
    ? [...purchasedLore, 'lore_secret_1']
    : purchasedLore;

  // Build combined activity log (user actions + purchase history)
  const combinedLogs = [
    ...logs,
    ...purchases.slice(0, 5).map((p: any) => {
      // Purchases can span shops — label each with THAT shop's currency word.
      const row = allShops.find((s: any) => addressesEqual(s.owner, p.coffeeShop));
      return {
        id: `p-${p.id}`,
        text: `STAMP AT SHOP ${p.coffeeShop?.slice(0, 6)}... +${p.pointsEarned} ${currencyLabel(row)}`,
        color: '#ffff00',
      };
    }),
  ].slice(0, 5);

  // Session/comp/birthday banners — always rendered above the stage,
  // regardless of which panel (if any) is open.
  const shellBanners = (
    <>
      {sessionBanner}
      {userAddress && <CompBanner customerAddress={userAddress} />}
      {birthdayPerkActive && (
        <div className="bg-accent-alt pixel-corners p-4 mb-6 text-center">
          <p className="font-heading text-[9px] text-gold-ink tracking-[1px]">IT'S YOUR BIRTHDAY WEEK!</p>
          <p className="font-grotesk font-medium text-[13px] text-gold-ink mt-1">
            {selectedShopRow?.birthday_perk?.title || 'A birthday treat'} at {currentAccount?.shopName || 'your shop'} — show this screen at the counter.
          </p>
        </div>
      )}
    </>
  );

  const passPanel = (
    <>
      <PixelPass
        purchases={currentAccount?.purchases || 0}
        stampIcon={selectedShopRow?.stamp_icon || 'default'}
        shopPixelImageUrl={selectedShopRow?.pixel_image_url || selectedShopRow?.pixelImageUrl}
        shopName={selectedShopRow ? resolveShopName(selectedShopRow) : undefined}
        streakWeeks={shopStreak}
        accentColor={selectedShopRow?.accent_color ?? null}
        streakRepairable={!!streakRepairable}
        streakRepairCost={STREAK_REPAIR_COST}
        canAffordRepair={availableFunBeans >= STREAK_REPAIR_COST}
        currencyLabel={shopCurrency}
        onRepairStreak={handleRepairStreak}
        // Member QR. Same shopId the wallet passes use — the Supabase uuid,
        // NOT the shop owner's address; the two are both "the shop" in
        // different code paths and the server only accepts the uuid.
        customerAddress={userAddress || undefined}
        shopId={selectedShopRow?.supabase_id || selectedShopRow?.id}
        freeEveryN={selectedShopRow?.free_every_n ?? null}
        embedded
      />
      <AddToGoogleWallet shopId={selectedShopRow?.supabase_id || selectedShopRow?.id} />
      <AddToAppleWallet shopId={selectedShopRow?.supabase_id || selectedShopRow?.id} />
    </>
  );

  const lootPanel = (
    <LootBag
      nfts={currentShopNFTs}
      onRedeem={handleRedeemNFT}
      pendingRedeemIds={pendingRedeemNftIds}
      collection={collection}
      redemptionCount={redemptionCount}
      mintCounts={mintCounts}
      resolveShopName={(sid) => {
        const s = allShops.find((x: any) => x.id === sid || x.supabase_id === sid);
        return s ? resolveShopName(s) : null;
      }}
      embedded
    />
  );

  const questsPanel = (
    <QuestCarousel
      quests={QUESTS}
      progressData={questProgressData}
      completedQuests={completedQuestIds}
      currencyLabel={shopCurrency}
      embedded
    />
  );

  const badgesPanel = <BadgeStrip userAddress={userAddress!} embedded />;

  const lorePanel = (
    <LoreArchive
      checkData={loreCheckData}
      currentBeans={availableFunBeans}
      onLoreUnlocked={(_id, title) => addLog(`LORE UNLOCKED: ${title}`, '#ff00ea')}
      onPurchaseLore={handlePurchaseLore}
      purchasedLoreIds={purchasedLoreIdsWithSecrets}
      loreDb={loreDbWithSecrets}
      currencyLabel={shopCurrency}
      embedded
    />
  );

  const morePanel = (
    <>
      {/* PlayerName is the only mount of the name/birthday editor — see the
          matching comment on lockedMorePanel above. */}
      <PlayerName shortAddress={shortAddress} userAddress={userAddress} onAvatarChange={setAvatarUrl} />
      <MorePanel
        logs={combinedLogs}
        statsLine={`${customerProfile?.total_purchases || 0} ${plural(customerProfile?.total_purchases || 0, 'STAMP')} · ${redemptionCount} FREE ${plural(redemptionCount, 'COFFEE')} · ${shopAccounts.length} ${plural(shopAccounts.length, 'SHOP')}`}
        hasOnChainShop={hasOnChainShop}
        onChangeShop={() => setShowShopModal(true)}
        onNavigate={(path) => navigate(path)}
        onLogout={logout}
      />
    </>
  );

  // HOME (round 5, Nik spec 2026-08-06): compact pass strip up top (tap →
  // PASS, where the full grid/QR/wallets live), then the shop-owner content
  // the grid used to crowd out — specials and beans are HOME's second act
  // (the business signal for founding shops). Mug station keeps the bottom,
  // centred in whatever space is left.
  //
  // `pending` reuses brewStation.isWaiting — the exact signal BrewStation's
  // own isWaiting gate receives, so the two never drift out of sync.
  //
  // Offline (#10b): `!online` drives StampCard's CACHED pill and hands
  // BrewStation into its own dedicated offline branch (grey inert mug +
  // "Try again" wired to loadData — the same function that already retries
  // a fetch everywhere else in this file, so "try again" genuinely retries).
  //
  // Session tangled (#9b): sessionBroken (the SAME flag that renders
  // SessionRecoveryBanner via `shellBanners` below) dims the card to
  // opacity-50 and hands BrewStation `paused`, which dims just the mug to
  // opacity-40 and swaps its prompt to "Stamping paused" — mirrors the
  // mockup's literal choice of dimming the card and the mug, not the whole
  // tab (shop logo/name stay at full opacity).
  const homeContent = (
    <>
      <div className={sessionBroken ? 'opacity-50' : undefined}>
        <StampCard
          purchases={currentAccount?.purchases || 0}
          pending={brewStation.isWaiting}
          rewardsTarget={stampsPerCard(selectedShopRow)}
          offline={!online}
          onOpenPass={() => selectTab('pass')}
        />
      </div>
      <ShopSpecials specials={selectedShopRow?.specials} />
      {/* Split row (Nik spec 2026-08-11). items-start matters: without it the
          shorter card stretches to match the taller one and the row grows for
          nothing, on a screen with ~35px of headroom total.

          The two-column grid is applied ONLY when both cards will actually
          render. A weekly goal is off by default, so every shop in prod was
          drawing "what we're pouring" squeezed into a half-width column with
          dead space beside it — Nik's "what we're pouring is on the left-hand
          side, that's why it looks weird". ShopGoal and ShopBeans each return
          null on empty data, and an empty grid cell holds its column, so the
          decision has to be made here where both inputs are visible. */}
      {(() => {
        const showGoal = !!selectedShopRow?.weekly_goal?.enabled
          && (selectedShopRow.weekly_goal.target || 0) > 0;
        const showBeans = (selectedShopRow?.beans?.length || 0) > 0;
        if (!showGoal && !showBeans) return null;
        return showGoal && showBeans ? (
          <div className="grid grid-cols-2 gap-2 items-start">
            <ShopBeans beans={selectedShopRow?.beans} compact />
            <ShopGoal goal={selectedShopRow?.weekly_goal} served={weeklyServed} />
          </div>
        ) : showGoal ? (
          <ShopGoal goal={selectedShopRow?.weekly_goal} served={weeklyServed} />
        ) : (
          // Full width, so `compact` is dropped too — the cramped micro-label
          // variant exists only to survive the ~167px column.
          <ShopBeans beans={selectedShopRow?.beans} />
        );
      })()}
      <ArcadeCard
        best={arcadeBest}
        rank={arcadeRank}
        tries={arcadeTries}
        onOpen={() => setArcadeOpen(true)}
      />
      {/* flex-1 centers the mug in the space between card and tab bar (mockup 5a) */}
      <div className="flex-1 flex flex-col justify-center">
        <BrewStation {...brewStation} offline={!online} paused={sessionBroken} onRetry={loadData} />
      </div>
    </>
  );

  // PASS: every old dock panel except MORE, in the order set by mockup #5b —
  // item boxes → quests → badges → lore. Each panel now carries its own
  // white-card section label internally (mockup 5b/10d put the label INSIDE
  // the card), so Dashboard just stacks them; TabShell's `main` supplies the
  // inter-section gap the same way it already does for homeContent.
  // PASS = progress: the pass itself, quests, badges. Item boxes + lore
  // moved to LOOT (Nik ruling 2026-08-05: tabs split by verb — earn /
  // progress / own-and-spend / admin).
  const passContent = (
    <>
      {passPanel}
      {questsPanel}
      {badgesPanel}
    </>
  );

  // LOOT = own & spend: item boxes (the live redemption path), the sponsor
  // card, and the lore collection (bean-purchasable — owning, not
  // progressing). Bean balance headlines in the CoralHeader. RewardsStore
  // was pulled 2026-08-06 (Nik): its bean-redeem flow isn't the product —
  // free coffees come from item boxes at the counter — and it never worked
  // with real beans.
  const lootContent = (
    <>
      {lootPanel}
      <BeanOfTheWeek feature={sponsorFeature} />
      {lorePanel}
    </>
  );

  // Offline header (#10b): grey variant, "LAST SYNCED {time}" (time omitted
  // until the first successful loadData() lands), "No connection" in the
  // title slot, "OFFLINE" where the shop name normally sits.
  const offlineEyebrow = lastSyncedAt ? `LAST SYNCED ${formatSyncTime(lastSyncedAt)}` : 'LAST SYNCED';

  return (
    <TabShell
      beanCount={availableFunBeans}
      eyebrow={online ? eyebrow : offlineEyebrow}
      levelTitle={online ? levelTitle : 'No connection'}
      statusLabel={online ? statusLabel : 'OFFLINE'}
      headerVariant={online ? 'coral' : 'grey'}
      chip={chip}
      xpPercent={xpPercent}
      homeContent={homeContent}
      passContent={passContent}
      lootContent={lootContent}
      moreContent={morePanel}
      banners={shellBanners}
      overlays={
        <>
          {shopModal}
          {squarePickModal}
          <LevelUpFlash show={showLevelUp} level={globalLevel} />
          <WelcomeFlash show={showWelcome} />
          <StampFlash show={stampGet.show} quantity={stampGet.quantity} currencyLabel={shopCurrency} accentColor={shopAccent} />

          {/* Bean Snake (Task 14). shopId is the Supabase uuid, same id
              PixelPass/AddToGoogleWallet/AddToAppleWallet already pass — the
              server only accepts that form, never the on-chain owner address. */}
          {arcadeOpen && (
            <ArcadePanel
              shopId={selectedShopRow?.supabase_id || selectedShopRow?.id || null}
              onClose={closeArcade}
            />
          )}

          {/* Counter redemption. Full-screen so the barista can read it at
              arm's length, and so nothing else is tappable while it is open. */}
          {counterRedeem && (
            <div
              className="fixed inset-0 z-50 bg-[rgba(28,25,23,.55)] flex items-center justify-center p-4"
              role="dialog"
              aria-modal="true"
              aria-label="Redeem free coffee"
            >
              <div className="w-full max-w-sm">
                {/* RewardCard stays mounted for the life of this modal, reveal
                    or no reveal. redeemedAt is LOCAL state inside RewardCard —
                    if the ternary that used to live here unmounted it whenever
                    `reveal` was set, dismissing the reveal ("NICE") remounted
                    a fresh RewardCard with redeemedAt back at null: state 1
                    ("BARISTA: TAP TO GIVE COFFEE") for an NFT that is already
                    burned, and a second tap would 409 against the now-spent
                    reward. Overlaying CollectibleReveal on top (instead of
                    swapping it in) keeps that state alive underneath. */}
                <div className="relative">
                  <div aria-hidden={reveal ? true : undefined}>
                    <RewardCard
                      nftId={counterRedeem.nftId}
                      shopId={counterRedeem.shopId}
                      shopName={counterRedeem.shopName}
                      logoUrl={counterRedeem.logoUrl}
                      itemTitle={counterRedeem.itemTitle}
                      onBurned={(requestId) => {
                        addLog('ITEM BOX BURNING — COFFEE IS YOURS', '#3dba4c');
                        // This tap's redemption is now the one allowed to paint a
                        // reveal — see counterRevealRequestId above.
                        counterRevealRequestId.current = requestId;
                        // The row is already approved server-side; burn the NFT
                        // on-chain with the customer's own session, as before.
                        void burnRedeemOnChain({ id: requestId, nft_id: counterRedeem.nftId } as RedeemRequestRow);
                      }}
                    />
                  </div>
                  {reveal && (
                    <div className="absolute inset-0 z-10">
                      <CollectibleReveal item={reveal} onDismiss={() => setReveal(null)} />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setCounterRedeem(null);
                    setReveal(null);
                    counterRevealRequestId.current = null; // closed — no longer eligible for a reveal
                  }}
                  className="pixel-corners-sm bg-white text-primary font-grotesk font-bold text-sm w-full py-3 mt-3"
                >
                  CLOSE
                </button>
              </div>
            </div>
          )}
        </>
      }
    />
  );
}

export default Dashboard;
